import React, { useState } from 'react';
import { RiDownloadCloud2Line, RiCheckLine, RiPauseFill, RiPlayFill, RiErrorWarningLine, RiCloseLine } from 'react-icons/ri';
import { useOffline } from './OfflineContext';
import { formatBytes } from './platform';
import styles from './OfflineButton.module.css';

// Per-book offline control for a library card: download, progress ring,
// pause/resume, downloaded tick (tap to remove).
export default function OfflineButton({ book, isCurated = false, disabled = false, size = 30 }) {
  const offline = useOffline();
  const [confirmRemove, setConfirmRemove] = useState(false);
  // Downloads are a feature of the installed app only; browser tabs never
  // show the control (their storage is separate and not persistent).
  if (!book?.id || !offline.standalone) return null;

  const state = offline.offlineStatusFor(book.id, isCurated);
  const stop = (event) => { event.preventDefault(); event.stopPropagation(); };

  const ringStyle = (fraction) => ({
    background: `conic-gradient(var(--primary-color) ${Math.round(Math.max(0, Math.min(1, fraction)) * 360)}deg, var(--border-color-strong) 0deg)`
  });

  if (confirmRemove) {
    return (
      <span className={styles.confirm} onClick={stop}>
        <span>Remove download?</span>
        <button className={styles.confirmYes} onClick={(e) => { stop(e); setConfirmRemove(false); offline.removeBook(state.key); }}>Yes</button>
        <button className={styles.confirmNo} onClick={(e) => { stop(e); setConfirmRemove(false); }} aria-label="Keep download"><RiCloseLine size={14} /></button>
      </span>
    );
  }

  switch (state.status) {
    case 'complete':
      return (
        <button
          type="button"
          className={`${styles.btn} ${styles.done}`}
          style={{ width: size, height: size }}
          title={`Downloaded · ${formatBytes(state.bytes)} · tap to remove`}
          aria-label="Downloaded, tap to remove"
          onClick={(e) => { stop(e); setConfirmRemove(true); }}
        >
          <RiCheckLine size={size * 0.55} />
        </button>
      );
    case 'preparing':
    case 'queued':
      return (
        <button type="button" className={`${styles.btn} ${styles.busy}`} style={{ width: size, height: size }} title="Waiting to download" aria-label="Waiting to download" onClick={(e) => { stop(e); offline.pauseDownload(state.key); }}>
          <span className={styles.spinner} />
        </button>
      );
    case 'downloading': {
      const count = Math.max(1, state.chapterCount || 1);
      const chapterFraction = state.chapterTotal ? state.chapterBytes / state.chapterTotal : 0;
      const fraction = ((state.chapterIndex || 0) + chapterFraction) / count;
      return (
        <button
          type="button"
          className={`${styles.btn} ${styles.ring}`}
          style={{ width: size, height: size, ...ringStyle(fraction) }}
          title={`Downloading chapter ${Math.min(count, (state.chapterIndex || 0) + 1)} of ${count} · ${formatBytes(state.bytes)} · tap to pause`}
          aria-label="Downloading, tap to pause"
          onClick={(e) => { stop(e); offline.pauseDownload(state.key); }}
        >
          <span className={styles.ringInner}><RiPauseFill size={size * 0.5} /></span>
        </button>
      );
    }
    case 'paused': {
      const count = Math.max(1, state.chapterCount || 1);
      const fraction = (state.chapterIndex || 0) / count;
      return (
        <button
          type="button"
          className={`${styles.btn} ${styles.ring} ${styles.paused}`}
          style={{ width: size, height: size, ...ringStyle(fraction) }}
          title="Download paused · tap to resume"
          aria-label="Download paused, tap to resume"
          onClick={(e) => { stop(e); offline.resumeDownload(state.key); }}
        >
          <span className={styles.ringInner}><RiPlayFill size={size * 0.5} /></span>
        </button>
      );
    }
    case 'error':
      return (
        <button
          type="button"
          className={`${styles.btn} ${styles.error}`}
          style={{ width: size, height: size }}
          title={`Download failed${state.error ? `: ${state.error}` : ''} · tap to retry`}
          aria-label="Download failed, tap to retry"
          onClick={(e) => {
            stop(e);
            const retry = window.confirm(`Download failed${state.error ? `:
${state.error}` : ''}

Try again?`);
            if (retry) offline.removeBook(state.key).then(() => offline.requestDownload(book, isCurated));
          }}
        >
          <RiErrorWarningLine size={size * 0.55} />
        </button>
      );
    default:
      return (
        <button
          type="button"
          className={styles.btn}
          style={{ width: size, height: size }}
          disabled={disabled}
          title="Download for offline listening"
          aria-label="Download for offline listening"
          onClick={(e) => { stop(e); offline.requestDownload(book, isCurated); }}
        >
          <RiDownloadCloud2Line size={size * 0.58} />
        </button>
      );
  }
}
